import React, { useState } from "react";
import { User, Mail } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import authService from "../../../Services/authService"; // Adjust path as needed

export default function CompleteProfile({ onSubmit, isLoading = false, phoneNumber }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    gender: "",
    age: "",
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const token = authService.getCurrentUsertoken();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

const validateForm = () => {
  const newErrors = {};

  // Name Validation
  const { name, age, gender, email } = formData;
  if (!name.trim()) {
    newErrors.name = "Name is required";
  } else {
    const onlyLetters = /^[A-Za-z\s]+$/.test(name); // Only letters and spaces
    const hasUpper = /[A-Z]/.test(name);
    const hasLower = /[a-z]/.test(name);

    if (!onlyLetters) {
      newErrors.name = "Name can only contain letters and spaces";
    } 
  }

  // Age Validation
  if (!age.trim()) {
    newErrors.age = "Age is required";
  }

  // Gender Validation
  if (!gender) {
    newErrors.gender = "Please select your gender";
  }

  // Email Validation
  if (email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const consecutiveDotRegex = /\.{2,}/;

    if (!emailRegex.test(email)) {
      newErrors.email = "Email is invalid";
    } else if (consecutiveDotRegex.test(email)) {
      newErrors.email = "Email address cannot contain consecutive dots";
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      console.log("Form submission failed due to validation errors.");
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(authService.getApiUrl() + "complete-profile", {
        method: "POST",
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=UTF-8',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: authService.getCurrentuser().id,
          user_name: formData.name,
          email: formData.email,
          gender: formData.gender,
          age: formData.age,
        }),
      });

      const result = await response.json();

      if (result.status !== "failed") {
        setErrors({});
        navigate(`/`);
      } else {
        setErrors({ phone_number: result.message || "Something went wrong." });
      }
    } catch (err) {
      console.error("Error sending profile:", err.message);
      setErrors({ phone_number: "Failed to send data. Please try again later." });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F7] flex flex-col">
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-sm border border-[#E0E0E0] overflow-hidden">
            <div className="p-6 border-b border-[#E0E0E0] flex flex-col items-center">
              <div className="mb-4 flex justify-center">
                <img
                  src="/images/pranaam-365-logo.png"
                  alt="Pranaam 365 Logo"
                  className="h-12 w-auto"
                />
              </div>
              <h1 className="text-2xl font-semibold text-[#4A4A4A]">Complete Profile</h1>
              <p className="text-sm text-[#4A4A4A]/70 text-center mt-2">
                Fill in your details to complete your registration.
              </p>
            </div>

            <div className="p-6">
              <form onSubmit={handleSubmit}>
                {/* Name */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Full Name <span className="text-[#951C25]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <User size={18} className="text-[#4A4A4A]/50" />
                    </div>
                    <input
                      type="text"
                      name="name"
                      className={`w-full pl-10 py-2.5 border ${errors.name ? "border-red-500" : "border-[#E0E0E0]"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                      placeholder="Enter your full name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  {errors.name && <p className="text-sm text-red-600 mt-1">{errors.name}</p>}
                </div>

                {/* Email */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Email (Optional)
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <Mail size={18} className="text-[#4A4A4A]/50" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      className={`w-full pl-10 py-2.5 border ${errors.email ? "border-red-500" : "border-[#E0E0E0]"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                      placeholder="Enter your email (optional)"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  {errors.email && <p className="text-sm text-red-600 mt-1">{errors.email}</p>}
                </div>

                {/* Gender */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Gender <span className="text-[#951C25]">*</span>
                  </label>
                  <select
                    name="gender"
                    className={`w-full py-2.5 px-3 border ${errors.gender ? "border-red-500" : "border-[#E0E0E0]"
                      } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25] bg-white`}
                    value={formData.gender}
                    onChange={handleChange}
                    required
                  >
                    <option value="">Select your gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && (
                    <p className="text-sm text-red-600 mt-1">{errors.gender}</p>
                  )}
                </div>

                <div className="mb-6">
                  <label className="block text-sm font-medium text-[#4A4A4A] mb-1">
                    Age <span className="text-[#951C25]">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                      <User size={18} className="text-[#4A4A4A]/50" />
                    </div>
                    <input
                      type="number"
                      name="age"
                      inputMode="numeric"
                      min="0"
                      className={`w-full pl-10 py-2.5 border ${errors.age ? "border-red-500" : "border-[#E0E0E0]"
                        } rounded-lg focus:outline-none focus:ring-2 focus:ring-[#951C25]/20 focus:border-[#951C25]`}
                      placeholder="Enter your age"
                      value={formData.age}
                      onChange={handleChange}
                      required
                    />
                    {errors.age && (
                      <p className="text-sm text-red-600 mt-1">{errors.age}</p>
                    )}
                  </div>
                </div>


                {/* Terms */}
                <div className="mb-6">
                  <label className="flex items-start">
                    <input
                      type="checkbox"
                      className="w-4 h-4 mt-0.5 rounded border-gray-300 text-[#951C25] focus:ring-[#951C25]"
                      required
                    />
                    <span className="ml-2 text-xs text-[#4A4A4A]/70">
                      By signing up, I agree to the{" "}
                      <Link to="/terms" className="text-[#951C25] hover:underline">
                        Terms of Service
                      </Link>{" "}
                      and{" "}
                      <Link to="/privacy" className="text-[#951C25] hover:underline">
                        Privacy Policy
                      </Link>
                      .
                    </span>
                  </label>
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  className="w-full bg-[#951C25] text-white py-3 rounded-lg font-medium hover:bg-[#951C25]/90 transition-colors flex items-center justify-center"
                  disabled={isLoading || isSubmitting}
                >
                  {isLoading || isSubmitting ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Creating Account...
                    </>
                  ) : (
                    "Sign Up"
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <footer className="bg-white py-4 border-t border-[#E0E0E0]">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-xs text-[#4A4A4A]/70">
              © 2025 Pranaam247. All rights reserved.
            </p>
            <div className="flex space-x-4 mt-2 md:mt-0">
              <Link to="/privacy" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
                Terms of Service
              </Link>
              <Link to="/help" className="text-xs text-[#4A4A4A]/70 hover:text-[#951C25]">
                Help Center
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
